import { Button, Dialog, DialogActions, DialogContent, DialogTitle, PaperProps, Theme } from "@mui/material";
import React, { ReactNode, type JSX } from "react";
import { useTranslation } from "react-i18next";
import FullScreenDialog from "../full-screen-dialog/FullScreenDialog";
import SettingsBar, { SettingsActionEnum } from "../settings-bar/SettingsBar";
import {isMobile, isTablet} from 'react-device-detect';
import { SxProps } from "@mui/system";

export type ResponsiveDialogCloseReason = "ok" | "cancel" | "backdropClick" | "escapeKeyDown";

export type ResponsiveDialogProps = {
    title: string | JSX.Element | null;
    enableOkay?: boolean;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
    fullWidth?: boolean;
    hideOkay?: boolean;
    okayLabel?: string | null;
    hideCancel?: boolean;
    cancelLabel?: string | null;
    open: boolean;
    onClose: (reason: ResponsiveDialogCloseReason) => void;
    children?: ReactNode;
    contentsx?: SxProps<Theme>;
    paperProps?: PaperProps;
    enableAdditionalAction?: boolean;
    additionalActionLabel?: string | null;
    onAdditionalAction?: () => void;
    dialogActions?: JSX.Element;
}

export default function ResponsiveDialog({
    title,
    enableOkay,
    hideOkay,
    maxWidth = "md",
    fullWidth,
    okayLabel,
    hideCancel,
    cancelLabel,
    open,
    onClose,
    contentsx,
    paperProps,
    enableAdditionalAction,
    additionalActionLabel,
    onAdditionalAction,
    dialogActions,
    children
}: ResponsiveDialogProps): JSX.Element {

    const { t } = useTranslation();
    const isPhone = isMobile && !isTablet;

    function onOkay() {
        onClose("ok");
    }

    function onCancel() {
        onClose("cancel");
    }

    return (
        <>
            {/* Desktop */}
            {!isPhone &&
                <Dialog open={open}
                    maxWidth={maxWidth}
                    fullWidth={fullWidth ?? true}
                    aria-labelledby="form-dialog-title"
                    onClose={(_event, reason) => onClose(reason)}
                    sx={{
                        height: "100%"
                    }}
                    slotProps={{
                        paper: paperProps
                    }}
                >

                    <DialogTitle>
                        {title}
                    </DialogTitle>

                    <DialogContent
                        sx={contentsx} >
                        {children}
                    </DialogContent>

                    {dialogActions ?? 
                    <DialogActions>
                        {enableAdditionalAction &&
                            <Button color="primary"
                            onClick={onAdditionalAction}
                        >
                            {additionalActionLabel}
                        </Button>
                    
                        }
                        {!hideCancel &&
                            <Button color="primary"
                                onClick={onCancel}
                            >
                                {cancelLabel ?? t("buttons.cancel")}
                            </Button>
                        }
                        {!hideOkay &&
                            <Button
                                color="secondary"
                                type="submit"
                                disabled={!enableOkay}
                                onClick={onOkay}
                            >
                                {okayLabel ?? t("buttons.okay")}
                            </Button>
                        }
                    </DialogActions>
                    }
                </Dialog>}
            {/* Mobile */}
            {isPhone &&
                <FullScreenDialog
                    aria-labelledby="form-dialog-title"
                    open={open}
                    onClose={onCancel}
                >
                    <SettingsBar
                        title={title}
                        okayLabel={okayLabel ?? undefined}
                        onClose={(action) => {
                            if (action === SettingsActionEnum.OK) {
                                onOkay();
                            } else {
                                onCancel();
                            }
                        }}
                        modified={enableOkay}
                        hideAction={hideOkay}
                    />

                    <DialogContent sx={contentsx}>
                        {children}
                    </DialogContent>

                </FullScreenDialog>
            }
        </>
    );
}



